import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { CONFIDENCE_HELP, ageText, confidenceLabel, freshnessNotices, kindLabel, lagNotice, shortSha } from "../../lib/architecture";

const TONES = {
  static: "bg-emerald-50 text-emerald-800 border-emerald-200",
  inferred: "bg-amber-50 text-amber-800 border-amber-200",
  unknown: "bg-slate-100 text-slate-700 border-slate-300",
};

// Confidence is words, never colour alone (accessibility): « lu dans le code », « déduit » or « non tranché », with its meaning in the tooltip.
export function ConfidenceBadge({ value, testid }) {
  const key = TONES[value] ? value : "unknown";
  return (
    <span title={CONFIDENCE_HELP[key]} data-testid={testid} data-confidence={key}
      className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-2xs font-medium ${TONES[key]}`}>
      {confidenceLabel(key)}
    </span>
  );
}

export function KindBadge({ kind }) {
  return <span className="inline-flex shrink-0 items-center rounded bg-slate-100 px-1.5 py-0.5 text-2xs font-medium text-slate-700">{kindLabel(kind)}</span>;
}

// path:line with a one-click copy: the way from the map to the source, since no code is stored in the snapshot.
export function SourceRef({ path, line, testid }) {
  const [done, setDone] = useState(false);
  if (!path) return <span className="text-xs text-slate-500">pas de fichier associé</span>;
  const text = line ? `${path}:${line}` : path;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Copie impossible : sélectionnez le chemin à la main.");
    }
  };
  return (
    <span className="inline-flex max-w-full items-center gap-1" data-testid={testid}>
      <code className="min-w-0 break-all rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">{text}</code>
      <button type="button" onClick={copy} aria-label={`Copier ${text}`}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-jamora focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11">
        {done ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      </button>
    </span>
  );
}

export function SnapshotBar({ snapshot, viewingOld, onLatest, lastScan }) {
  const notices = freshnessNotices(snapshot);
  const lag = lagNotice(lastScan);
  return (
    <div className="space-y-2" data-testid="snapshot-bar">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
        <span>Version du dépôt <code className="rounded bg-slate-100 px-1 text-slate-800" data-testid="snapshot-commit">{shortSha(snapshot.commit_sha)}</code>{snapshot.branch ? ` (${snapshot.branch})` : ""}</span>
        <span data-testid="snapshot-age">{ageText(snapshot.age_days)}</span>
        <span>{snapshot.counts?.nodes?.toLocaleString("fr-FR")} éléments · {snapshot.counts?.edges?.toLocaleString("fr-FR")} relations · {snapshot.counts?.unknowns?.toLocaleString("fr-FR")} à investiguer</span>
        {viewingOld ? (
          <button type="button" onClick={onLatest} className="font-medium text-jamora hover:underline" data-testid="snapshot-latest">Revenir à la version courante</button>
        ) : null}
      </div>
      {viewingOld ? (
        <div role="status" className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="snapshot-old">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>Vous consultez une ancienne version : les chemins et les lignes renvoient au commit {shortSha(snapshot.commit_sha)}, pas au code actuel.</span>
        </div>
      ) : null}
      {lag ? (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900" data-testid="snapshot-lag">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>{lag}</span>
        </div>
      ) : null}
      {notices.map((n) => (
        <div key={n.text} role="status" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="snapshot-notice">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>{n.text}</span>
        </div>
      ))}
    </div>
  );
}
